/**
 * useAbandonedBelongingsReports — query the
 * abandoned_belongings_reports table.
 *
 * Scope is enforced by RLS. Returns rows ordered by created_at DESC.
 * Cleaner-side TaskDetailPage filters by cleaningTaskId; the
 * supervisor queue (#75b) will use statuses.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type AbandonedBelongingsStatus =
  | 'reported'
  | 'acknowledged'
  | 'removed'
  | 'billed'

export interface AbandonedBelongingsReportRow {
  id:                       string
  organization_id:          string
  cleaning_task_id:         string
  room_id:                  string
  reported_by_user_id:      string
  status:                   AbandonedBelongingsStatus
  description:              string
  photo_storage_paths:      string[]
  acknowledged_by_user_id:  string | null
  acknowledged_at:          string | null
  removed_at:               string | null
  billed_at:                string | null
  disposition_notes:        string | null
  created_at:               string
  updated_at:               string
}

export interface UseAbandonedBelongingsReportsArgs {
  cleaningTaskId?: string
  statuses?:       AbandonedBelongingsStatus[]
}

/** Open-queue presets the supervisor surface will use by default (#75b). */
export const OPEN_QUEUE_STATUSES: readonly AbandonedBelongingsStatus[] = [
  'reported',
  'acknowledged',
] as const

export function useAbandonedBelongingsReports(
  args: UseAbandonedBelongingsReportsArgs = {},
): UseQueryResult<AbandonedBelongingsReportRow[]> {
  const supabase = useSupabase()
  const cleaningTaskId = args.cleaningTaskId
  const statuses       = args.statuses

  return useQuery({
    queryKey: ['abandoned-belongings', { cleaningTaskId: cleaningTaskId ?? null, statuses: statuses ?? null }],
    refetchInterval: cleaningTaskId ? false : 30_000,
    queryFn: async (): Promise<AbandonedBelongingsReportRow[]> => {
      let query = supabase
        .from('abandoned_belongings_reports')
        .select(
          'id, organization_id, cleaning_task_id, room_id, reported_by_user_id, status,' +
          ' description, photo_storage_paths,' +
          ' acknowledged_by_user_id, acknowledged_at, removed_at, billed_at, disposition_notes,' +
          ' created_at, updated_at',
        )
        .order('created_at', { ascending: false })

      if (cleaningTaskId) {
        query = query.eq('cleaning_task_id', cleaningTaskId)
      }
      if (statuses && statuses.length > 0) {
        query = query.in('status', statuses as unknown as string[])
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as unknown as AbandonedBelongingsReportRow[]
    },
  })
}
